"use client";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import Chip from "@/components/global/ui/Chip";
import { cn } from "@/lib/utils";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import arrow from "@/public/icons/arrow.svg";
import arrowUp from "@/public/icons/arrow-up.svg";
import Image from "next/image";

const TRANSITION_EASING = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_CTA = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_ITEM = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const ACCORDION_VARIANTS = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING] };
const TITLE_TRANSITION = { duration: 0.8, ease: [...TRANSITION_EASING], delay: 0.1 };
const CTA_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING], delay: 0.2 };
const ITEM_TRANSITION_BASE = { duration: 0.6, ease: [...TRANSITION_EASING] };
const ACCORDION_TRANSITION = {
  height: { duration: 0.5, ease: [...TRANSITION_EASING] },
  opacity: { duration: 0.35, ease: "easeOut" },
};

function FAQSectionDesktop({ data }: { data: PageHomeSchema }) {
  const { faqChip, faqTitle, faqCTA, faqQuestions } = data;
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="hidden lg:block relative py-[8rem] bg-[#040404]">
      <div className="w-[min(180rem,100%)] flex flex-row gap-[4rem] mx-auto justify-between">
        <div className="w-[min(55.75rem,100%)] flex flex-col justify-between">
          <div>
            {faqChip && (
              <motion.div
                className="w-fit [&_*]:text-white mb-[3rem]"
                {...REVEAL_CHIP}
                transition={CHIP_TRANSITION}>
                <Chip
                  text={faqChip}
                  animatedIcon
                  spinnerSize={32}
                  spinnerFps={12}
                  spinnerVariant="wave"
                />
              </motion.div>
            )}

            {faqTitle && (
              <motion.div
                className="richtext richtext-start prose-h1:text-white prose-h1:tracking-normal prose-h1:text-[8rem]"
                {...REVEAL_TITLE}
                transition={TITLE_TRANSITION}>
                <PortableText
                  value={faqTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}

            {faqCTA && (
              <motion.div
                className="mt-[4rem]"
                {...REVEAL_CTA}
                transition={CTA_TRANSITION}>
                <CTAButton data={faqCTA} />
              </motion.div>
            )}
          </div>
        </div>

        {faqQuestions && faqQuestions.length > 0 && (
          <div className="flex flex-col w-[min(88rem,100%)]">
            {faqQuestions.map((item, index) => (
              <motion.div
                key={index}
                className={`border-b border-white/10 last:border-none px-[2rem] py-[3rem] transition-colors duration-500 ${openIndex === index ? "bg-white/5" : ""}`}
                {...REVEAL_ITEM}
                transition={{ ...ITEM_TRANSITION_BASE, delay: index * 0.05 }}>
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className={`flex justify-between items-center w-full text-left cursor-pointer transition-all duration-300 ${openIndex === index ? "mb-[2rem]" : ""}`}>
                  <span
                    className={`leading-tight text-white text-[2rem] uppercase transition-all duration-300  ${openIndex === index ? "" : "opacity-60"}`}>
                    {index + 1}. {item.question}
                  </span>
                  <span
                    className={cn(
                      "text-[2.5rem] leading-none shrink-0 text-white transition-transform duration-300 ",
                      openIndex === index ? "rotate-0 " : "rotate-180 opacity-60",
                    )}>
                    <Image
                      src={arrowUp}
                      alt=""
                    />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {openIndex === index && (
                    <motion.div
                      {...ACCORDION_VARIANTS}
                      transition={ACCORDION_TRANSITION}
                      className="flex items-start overflow-clip gap-[1rem]">
                      <Image
                        src={arrow}
                        alt=""
                        className="shrink-0 pt-[.25rem]"
                      />
                      <div className="richtext richtext-start prose-p:text-white prose-p:leading-[150%]">
                        <PortableText
                          value={item.answer}
                          components={richTextComponent}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FAQSectionMobile({ data }: { data: PageHomeSchema }) {
  const { faqChip, faqTitle, faqCTA, faqQuestions } = data;
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="lg:hidden relative py-[8rem] bg-[#040404] px-[3rem]">
      <div className="w-[min(180rem,100%)] flex flex-col gap-[4rem] mx-auto justify-between">
        <div className="w-[min(55.75rem,100%)] flex flex-col justify-between">
          <div>
            {faqChip && (
              <div className="w-fit [&_*]:text-white mb-[3rem]">
                <Chip
                  text={faqChip}
                  spinnerSize={32}
                  spinnerFps={12}
                  spinnerVariant="wave"
                />
              </div>
            )}

            {faqTitle && (
              <div className="richtext richtext-start prose-h1:text-white prose-h1:text-[5rem] prose-h1:tracking-normal">
                <PortableText
                  value={faqTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            {faqCTA && (
              <div className="mt-[4rem]">
                <CTAButton data={faqCTA} />
              </div>
            )}
          </div>
        </div>

        {faqQuestions && faqQuestions.length > 0 && (
          <div className="flex flex-col w-[min(88rem,100%)]">
            {faqQuestions.map((item, index) => (
              <div
                key={index}
                className={`border-b border-white/10 last:border-none px-[2rem] py-[3rem] transition-colors duration-500 ${openIndex === index ? "bg-white/5" : ""}`}>
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className={`flex justify-between items-center w-full text-left cursor-pointer transition-all duration-300 ${openIndex === index ? "mb-[2rem]" : ""}`}>
                  <span
                    className={`leading-tight text-white text-[2rem] uppercase transition-all duration-300 ${openIndex === index ? "" : "opacity-60"}`}>
                    {index + 1}. {item.question}
                  </span>
                  <span
                    className={cn(
                      "text-[2.5rem] leading-none shrink-0 text-white transition-transform duration-300",
                      openIndex === index ? "rotate-0 " : "rotate-180 opacity-60",
                    )}>
                    <Image
                      src={arrowUp}
                      alt=""
                    />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {openIndex === index && (
                    <motion.div
                      {...ACCORDION_VARIANTS}
                      transition={ACCORDION_TRANSITION}
                      className="flex items-start overflow-clip gap-[1rem]">
                      <Image
                        src={arrow}
                        alt=""
                        className="shrink-0 pt-[.25rem]"
                      />
                      <div className="richtext richtext-start prose-p:text-white prose-p:leading-[150%]">
                        <PortableText
                          value={item.answer}
                          components={richTextComponent}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function FAQSection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <FAQSectionDesktop data={data} />
      <FAQSectionMobile data={data} />
    </>
  );
}
